import React, { useState } from 'react';
import courtReserveLogo from '../../assets/integrations/courtreserve.svg';
import { API_URL, getAuthToken, getVenueQuery } from './internalIntegrationUtils';

type Step = 'form' | 'saving' | 'success' | 'error';

type TestStepResult = {
  name: string;
  status: 'pass' | 'fail';
  message: string;
  detail?: string;
};

type TestResult = {
  ok: boolean;
  steps: TestStepResult[];
  facilityId?: string;
};

const TEST_STEP_LABELS: Record<string, string> = {
  inputs: 'Inputs',
  parse_facility_id: 'Parse facility ID',
  login: 'Sign into CourtReserve',
  list_reservations: 'List facility reservations',
  cancel_route: 'Cancel route reachable',
};

export interface CourtReserveAutoApprovalIntegrationRecord {
  id: string;
  external_id: string;
  config?: {
    courtreserveUrl?: string;
    adminEmail?: string;
    adminPassword?: string;
    autoApprovalEnabled?: boolean;
    reservationTypeId?: number;
    lastExecutionAt?: string | null;
    successCount?: number;
    failureCount?: number;
  };
  last_sync_at?: string | null;
}

function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

async function updateCourtReserveAutoApprovalIntegration(
  integrationId: string,
  config: CourtReserveAutoApprovalIntegrationRecord['config']
) {
  const token = getAuthToken();
  const res = await fetch(`${API_URL}/api/integrations/${integrationId}${getVenueQuery()}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ config }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update integration: ${res.status}`);
  }

  return res.json();
}

export function CourtReserveAutoApprovalConnectedCard({
  integration,
  onDisconnect,
  onRefresh,
}: {
  integration: CourtReserveAutoApprovalIntegrationRecord;
  onDisconnect: () => void;
  onRefresh: () => void;
}) {
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(
    integration.config?.autoApprovalEnabled !== false
  );
  const [savingToggle, setSavingToggle] = useState(false);
  const [reservationTypeIdInput, setReservationTypeIdInput] = useState(
    integration.config?.reservationTypeId != null ? String(integration.config.reservationTypeId) : ''
  );
  const [savingTypeId, setSavingTypeId] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    steps: Array<{ name: string; status: 'pass' | 'fail'; message: string; detail?: string }>;
  } | null>(null);
  const [testError, setTestError] = useState('');

  const adminEmail = integration.config?.adminEmail || integration.external_id;
  const lastExecutionAt = integration.config?.lastExecutionAt || integration.last_sync_at || null;

  const handleTest = async () => {
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${API_URL}/api/integrations/${integration.id}/test-courtreserve-auto-approval${getVenueQuery()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Test failed: ${res.status}`);
      }
      setTestResult(data);
    } catch (err: any) {
      setTestError(err.message || 'Test failed.');
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async () => {
    const nextValue = !autoApprovalEnabled;
    setSavingToggle(true);
    setError('');

    try {
      await updateCourtReserveAutoApprovalIntegration(integration.id, {
        ...(integration.config || {}),
        autoApprovalEnabled: nextValue,
      });
      setAutoApprovalEnabled(nextValue);
      onRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to update auto-approval setting.');
    } finally {
      setSavingToggle(false);
    }
  };

  const handleSaveReservationTypeId = async () => {
    setSavingTypeId(true);
    setError('');
    const trimmed = reservationTypeIdInput.trim();
    const nextValue = trimmed === '' ? undefined : Number(trimmed);
    if (nextValue !== undefined && (!Number.isInteger(nextValue) || nextValue <= 0)) {
      setError('Reservation Type ID must be a positive integer.');
      setSavingTypeId(false);
      return;
    }
    try {
      const nextConfig = { ...(integration.config || {}) };
      if (nextValue === undefined) {
        delete (nextConfig as { reservationTypeId?: number }).reservationTypeId;
      } else {
        (nextConfig as { reservationTypeId?: number }).reservationTypeId = nextValue;
      }
      await updateCourtReserveAutoApprovalIntegration(integration.id, nextConfig);
      onRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to update reservation type override.');
    } finally {
      setSavingTypeId(false);
    }
  };

  const successCount = integration.config?.successCount;
  const failureCount = integration.config?.failureCount;
  const hasRunStats = typeof successCount === 'number' || typeof failureCount === 'number';

  return (
    <div className="integration-card integration-card--connected">
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          <img className="integration-logo__img" src={courtReserveLogo} alt="CourtReserve logo" />
        </div>
        <div className="integration-title">
          <h3>CourtReserve Auto-Approval</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">Booking automation</span>
          </div>
        </div>
        <div className="integration-card-header__right">
          <span className="integration-state-pill integration-state-pill--connected">
            <span className="integration-state-pill__dot" aria-hidden="true" />
            Connected
          </span>
        </div>
      </div>

      <dl className="integration-stats">
        <div className="integration-stats__item">
          <dt>Account</dt>
          <dd className="integration-stats__value integration-stats__value--mono">{adminEmail}</dd>
        </div>
        <div className="integration-stats__item">
          <dt>Last run</dt>
          <dd className="integration-stats__value">{formatTimeAgo(lastExecutionAt)}</dd>
        </div>
        {hasRunStats && (
          <div className="integration-stats__item">
            <dt>Runs</dt>
            <dd className="integration-stats__value">
              {successCount ?? 0} ok
              {(failureCount ?? 0) > 0 && (
                <>
                  {' · '}
                  <span className="integration-stats__value--warn">{failureCount} failed</span>
                </>
              )}
            </dd>
          </div>
        )}
      </dl>

      {error && (
        <div className="integration-error-banner" role="alert">{error}</div>
      )}

      <section className="integration-section">
        <div className="integration-section__head">
          <div>
            <div className="integration-section__title">Test connection</div>
            <div className="integration-section__subtitle">
              Replays saved credentials. Nothing is created or canceled.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost integration-section__action"
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? 'Testing…' : 'Run test'}
          </button>
        </div>

        {testError && (
          <div className="integration-test-error" role="alert">{testError}</div>
        )}

        {testResult && (
          <ul className="integration-test-steps">
            {testResult.steps.map((s, i) => (
              <li key={`${s.name}-${i}`} className={`integration-test-step integration-test-step--${s.status}`}>
                <span className="integration-test-step__icon" aria-hidden="true">
                  {s.status === 'pass' ? '✓' : '✕'}
                </span>
                <span className="integration-test-step__name">{TEST_STEP_LABELS[s.name] || s.name}</span>
                <span className="integration-test-step__message">{s.message}</span>
                {s.detail && <pre className="integration-test-step__detail">{s.detail}</pre>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="integration-section integration-section--row">
        <div>
          <div className="integration-section__title">Auto-Approval</div>
          <div className="integration-section__subtitle">
            When on, the worker creates and approves matching bookings.
          </div>
        </div>
        <label className={`switch ${savingToggle ? 'switch--saving' : ''}`}>
          <input
            type="checkbox"
            checked={autoApprovalEnabled}
            onChange={handleToggle}
            disabled={savingToggle}
          />
          <span className="switch__track" aria-hidden="true" />
          <span className="switch__label">
            {savingToggle ? 'Saving…' : autoApprovalEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </section>

      <section className="integration-section">
        <div className="integration-section__title">Reservation Type override</div>
        <div className="integration-section__subtitle">
          Optional. Numeric CourtReserve ReservationType ID for bookings we create.
          Leave blank for default. Use this to pick a type whose theme color
          matches how the partner wants PickleCourts bookings to look.
        </div>
        <div className="integration-section__row">
          <input
            type="text"
            className="form-input"
            placeholder="e.g. 78940"
            value={reservationTypeIdInput}
            onChange={(e) => setReservationTypeIdInput(e.target.value)}
            disabled={savingTypeId}
            inputMode="numeric"
            pattern="[0-9]*"
            style={{ maxWidth: 180 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleSaveReservationTypeId}
            disabled={
              savingTypeId
              || reservationTypeIdInput.trim() === String(integration.config?.reservationTypeId ?? '')
            }
          >
            {savingTypeId ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <div className="integration-card-footer">
        <button className="btn btn-link integration-disconnect" onClick={onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}

export function CourtReserveAutoApprovalAvailableCard({ onSetup }: { onSetup: () => void }) {
  return (
    <div className="integration-card integration-card--available">
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          <img className="integration-logo__img" src={courtReserveLogo} alt="CourtReserve logo" />
        </div>
        <div className="integration-title">
          <h3>CourtReserve Auto-Approval</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">Booking automation</span>
          </div>
        </div>
        <span className="integration-badge available">Available</span>
      </div>

      <p className="integration-description">
        Sign in to CourtReserve, create the reservation, and approve the booking — without admin intervention.
      </p>

      <div className="integration-card-footer">
        <button className="btn btn-primary" onClick={onSetup}>Connect</button>
      </div>
    </div>
  );
}

export default function CourtReserveAutoApprovalIntegration({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('form');
  const [courtreserveUrl, setCourtreserveUrl] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState('');

  const token = getAuthToken();

  const handleTest = async () => {
    if (!courtreserveUrl.trim() || !adminEmail.trim() || !adminPassword.trim()) {
      setTestError('Fill in URL, email, and password before testing.');
      setTestResult(null);
      return;
    }
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/integrations/courtreserve_auto_approval/test${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          courtreserveUrl: courtreserveUrl.trim(),
          adminEmail: adminEmail.trim(),
          adminPassword: adminPassword.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Test failed: ${res.status}`);
      }
      setTestResult(data as TestResult);
    } catch (err: any) {
      setTestError(err.message || 'Test failed.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!courtreserveUrl.trim() || !adminEmail.trim() || !adminPassword.trim()) {
      setError('All fields are required.');
      return;
    }

    setError('');
    setStep('saving');

    try {
      const res = await fetch(`${API_URL}/api/integrations${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          integrationType: 'courtreserve_auto_approval',
          externalId: adminEmail.trim(),
          config: {
            courtreserveUrl: courtreserveUrl.trim(),
            adminEmail: adminEmail.trim(),
            adminPassword: adminPassword.trim(),
            autoApprovalEnabled: true,
            maxRetries: 5,
            courtMappings: {},
          },
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to save: ${res.status}`);
      }

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Failed to save integration.');
      setStep('error');
    }
  };

  const headerBlock = (subtitle: string) => (
    <div className="integration-setup__header">
      <button className="integration-setup__back" onClick={onClose}>← Back</button>
      <div className="integration-setup__title">
        <div
          className="integration-setup__logo"
          style={{ background: 'white', padding: 8, border: '1px solid var(--gray-200)' }}
        >
          <img
            src={courtReserveLogo}
            alt="CourtReserve"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
        <div>
          <h1>CourtReserve Auto-Approval</h1>
          <p>{subtitle}</p>
        </div>
        <span className="integration-badge beta" style={{ marginLeft: 12 }}>Internal</span>
      </div>
    </div>
  );

  if (step === 'success') {
    return (
      <div className="integration-setup">
        {headerBlock('Booking automation connected')}
        <div className="integration-setup__success">
          <div className="integration-setup__success-icon">✓</div>
          <h2>Auto-Approval Connected</h2>
          <p>Provisioned account: <strong>{adminEmail}</strong></p>
          <p>Bookings for this venue can now be auto-created in CourtReserve and approved without manual admin work.</p>
          <div className="integration-step__actions">
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-setup">
      {headerBlock('Create CourtReserve reservations and approve bookings automatically')}

      <div className="integration-step">
        <div className="integration-step__card">
          <h2>Connect CourtReserve Admin Access</h2>
          <p>
            Save the CourtReserve booking URL and admin credentials for this venue. The automation worker
            will use this account to create reservations and approve pending bookings.
          </p>

          <div className="integration-setup__field">
            <label htmlFor="cr-aa-url">CourtReserve Instance URL</label>
            <input
              id="cr-aa-url"
              type="url"
              className="form-input"
              placeholder="https://app.courtreserve.com/Online/Reservations/Bookings/12345"
              value={courtreserveUrl}
              onChange={(e) => setCourtreserveUrl(e.target.value)}
              autoComplete="off"
            />
            <span className="integration-setup__field-hint">
              Use the full booking URL for the venue&apos;s CourtReserve instance.
            </span>
          </div>

          <div className="integration-setup__field">
            <label htmlFor="cr-aa-email">Admin Email</label>
            <input
              id="cr-aa-email"
              type="email"
              className="form-input"
              placeholder="luke@picklecourts.rent"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="integration-setup__field">
            <label htmlFor="cr-aa-password">Admin Password</label>
            <input
              id="cr-aa-password"
              type="password"
              className="form-input"
              placeholder="••••••••"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              autoComplete="new-password"
            />
            <span className="integration-setup__field-hint">
              Stored in integration config for browser automation. Use Test connection below to verify before saving.
            </span>
          </div>

          <section className="integration-section">
            <div className="integration-section__head">
              <div>
                <div className="integration-section__title">Test connection</div>
                <div className="integration-section__subtitle">
                  Logs in, lists reservations, checks the cancel route. Nothing is created.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost integration-section__action"
                onClick={handleTest}
                disabled={testing || step === 'saving'}
              >
                {testing ? 'Testing…' : 'Run test'}
              </button>
            </div>

            {testError && (
              <div className="integration-test-error" role="alert">{testError}</div>
            )}

            {testResult && (
              <ul className="integration-test-steps">
                {testResult.steps.map((s, i) => (
                  <li key={`${s.name}-${i}`} className={`integration-test-step integration-test-step--${s.status}`}>
                    <span className="integration-test-step__icon" aria-hidden="true">
                      {s.status === 'pass' ? '✓' : '✕'}
                    </span>
                    <span className="integration-test-step__name">{TEST_STEP_LABELS[s.name] || s.name}</span>
                    <span className="integration-test-step__message">{s.message}</span>
                    {s.detail && <pre className="integration-test-step__detail">{s.detail}</pre>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error && <div className="integration-setup__error" role="alert">{error}</div>}

          <div className="integration-step__actions">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={step === 'saving' || testing}
              title={!testResult?.ok ? 'Recommended: run Test connection first' : undefined}
            >
              {step === 'saving' ? 'Connecting…' : 'Connect Auto-Approval'}
            </button>
          </div>
        </div>

        <div className="integration-step__help">
          <h3>How auto-approval works</h3>
          <p>
            When a customer requests a booking on PickleCourts, the worker signs into CourtReserve as
            this admin user, creates the matching reservation, and flips the request to approved —
            no manual portal work.
          </p>

          <h4>Where to find the booking URL</h4>
          <ol>
            <li>Sign in to your CourtReserve admin console.</li>
            <li>Open <strong>Reservations → Make a Reservation</strong>.</li>
            <li>Copy the full URL from the address bar — it ends in <code>/Bookings/&lt;id&gt;</code>.</li>
          </ol>

          <h4>What gets stored</h4>
          <p>
            The URL, admin email, and password live in this integration&apos;s encrypted config and are
            only used by the automation worker. You can disable auto-approval at any time without
            removing the credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
